import type { ComponentType, ReactNode } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Keyboard,
  Pressable,
  Text,
  TextInput,
  View,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import {
  EquipmentKind,
  MuscleRegion,
  type Exercise,
  type ExerciseGroup as ExerciseGroupT,
  type ListCache,
  type LocalizedText,
} from '@jackedlogs/shared';

import type { ProgramApiFetch } from '../ProgramEditor/types';
import type { ConfirmFn } from '../seams';
import { resolveTheme } from '../theme';
import { useThemePreferenceContext } from '../ThemeContext';
import { EmptyState } from '../primitives/EmptyState';
import { PickerSessionBand } from '../primitives/PickerSessionBand';
import { FilterChipsRow } from './FilterChipsRow';
import { FiltersToggle } from './FiltersToggle';
import { ExercisePickerRow } from './ExercisePickerRow';
import { SupersetPanel, type GroupOption, type SupersetContext } from './SupersetPanel';
import { makeStyles } from './pickerStyles';
import {
  useExercisePickerState,
  type PickerTab,
} from './useExercisePickerState';

export type { GroupOption, PairEntry, SupersetContext } from './SupersetPanel';

// Unified Add-Exercise picker (inbox 963ca6f2). Two tabs — Esercizi / Superset
// — share ONE multi-select exercise list (recency-ordered, favourites pinned
// on top). The Superset tab layers a panel above the list (current exercise,
// join existing, pair in-workout, saved templates) and its footer creates a
// group in one shot. Opening from a card's ＋ defaults to the Superset tab.
//
// Shared component (P2 of the scheduler-composer plan): this is the picker
// CONTENT — the host owns the presentation shell (mobile wraps it in a
// pageSheet Modal + SafeAreaView; web-rnw mounts it in its own modal) and
// must render it inside a flex:1 column. Platform concerns arrive as props:
// apiFetch + an optional ListCache (offline catalog mirror), the bookmarks
// store, saved superset templates, the recency loader, the locale resolver
// and the pain-risk dot.

// Favourite/pinned-exercise store contract (mobile: useBookmarks —
// cache-first read + optimistic toggle; the picker only reads + flips).
export type PickerBookmarks = {
  isBookmarked: (slug: string) => boolean;
  toggle: (slug: string) => void;
  // Primitive memo key: changes only on pin/unpin, so the sorted list
  // doesn't re-sort on unrelated renders of a fresh store object.
  count: number;
};

export type ExercisePickerProps = {
  visible: boolean;
  onClose: () => void;
  /** Single-pick mode (e.g. the rest-defaults settings card): tap a row →
   *  pick that one exercise + close. When set, the tabs / multi-select footer
   *  / superset panel are all hidden — it's a plain one-shot picker. */
  onPickSingle?: (ex: Exercise) => void;
  /** Add N exercises (by slug) as standalone entries. */
  onAddMany?: (slugs: string[]) => void;
  /** Create a superset: add new members (addSlugs) + group with the current
   *  entry and any picked already-present entries (pairEntryIdxs). */
  onCreateSuperset?: (args: {
    addSlugs: string[];
    pairEntryIdxs: number[];
    name: string;
  }) => void;
  /** Join the current entry to an existing superset. */
  onJoinGroup?: (group: GroupOption) => void;
  /** Present when opened from a card's ＋ — drives the Superset panel + the
   *  default tab. */
  supersetContext?: SupersetContext | null;
  /** Slugs already on the draft. What the picker DOES with them is
   *  `presentTreatment` — the list itself is the same question everywhere. */
  presentSlugs?: ReadonlyArray<string>;
  /** LG-41 — how an already-present exercise is treated.
   *
   *  It used to be hidden, everywhere and silently. That assumed an exercise
   *  appears once, which is the premise LG-39 already dropped inside a
   *  workout: *«se ho walking, non posso aggiungere walking, non c'è nella
   *  lista»*. So it is now MARKED and stays selectable, and the mark carries
   *  a word (never colour alone) that says where it already is — hence one
   *  prop for the treatment AND the wording, so "mark" can't be asked for
   *  without saying in which context.
   *
   *  `hide` survives for PLANS only, and not by preference: a
   *  PlannedExerciseEntry has no id (plan.ts), so two entries with the same
   *  slug are ambiguous to everything that resolves by slug downstream
   *  (LG-40). It goes when the plan entry gets a stable id. */
  presentTreatment?: 'mark-workout' | 'mark-program' | 'hide';

  // --- host adapters ---
  /** Drives the palette; host owns theme resolution. */
  dark: boolean;
  /** App-provided API client (attaches the Cognito ID token). */
  apiFetch: ProgramApiFetch;
  /** Optional offline catalog mirror (mobile: the AsyncStorage adapter).
   *  Read cache-first on open, written back after a fresh fetch. */
  cache?: ListCache<Exercise>;
  /** Per-slug recency (slug → latest startedAt) for the "last logged first"
   *  ordering; called on each open. Omit → plain name order. */
  loadRecency?: () => Promise<Map<string, string>>;
  /** Favourites (the ⭐ on each row). */
  bookmarks: PickerBookmarks;
  /** Saved superset templates shown on the Superset tab. */
  templates: ReadonlyArray<ExerciseGroupT>;
  onRemoveTemplate: (id: string) => void;
  /** RW-25 — the host's confirm seam, for the saved-superset delete on the
   *  Superset tab. It used react-native's Alert, a no-op on react-native-web,
   *  and the web composer already wires `onRemoveTemplate` (PlanScreen), so
   *  that delete was reachable and silently did nothing. Every host has one
   *  implementation already (confirmViaAlert / confirmViaWindow). */
  confirm: ConfirmFn;
  /** Locale resolver for exercise display names. */
  getExerciseName: (name: LocalizedText) => string;
  /** Pain-risk dot rendered before each row name (mobile: PainDot). */
  PainDot?: ComponentType<{ exercise: Exercise }>;
  /** What the dot SAYS, for the row's own accessibilityLabel — same
   *  host-injected slot hostDeps.ts's ExerciseSwapPicker already has. */
  usePainRiskLabel?: (exercise: Exercise) => string | null;
  /** EQ-1 slice D2b — the training-place chip, as two nodes from `useGymChip`
   *  (mobile: useLoggerGymChip). The trigger takes the header's right cell,
   *  the tray opens full-width under the row. Passed in rather than built
   *  here because the place lives in the host's logger draft, and because
   *  this same picker mounts on web-rnw and in the rest-defaults card, where
   *  there is no session to place. Omit it and the header is unchanged. */
  gymChip?: { trigger: ReactNode; tray: ReactNode };
};

export function ExercisePicker({
  visible,
  onClose,
  onPickSingle,
  onAddMany,
  onCreateSuperset,
  onJoinGroup,
  supersetContext = null,
  presentSlugs = [],
  presentTreatment = 'hide',
  dark,
  apiFetch,
  cache,
  loadRecency,
  bookmarks,
  templates,
  confirm,
  onRemoveTemplate,
  getExerciseName,
  PainDot,
  usePainRiskLabel,
  gymChip,
}: ExercisePickerProps) {
  const singleMode = onPickSingle !== undefined;
  const family = useThemePreferenceContext()?.preference.family ?? 'brand';
  const theme = resolveTheme(dark, family);
  const { t } = useTranslation();

  const {
    tab,
    setTab,
    query,
    setQuery,
    regions,
    equipment,
    toggleRegion,
    toggleEquipment,
    selected,
    onRowPress,
    pairIdxs,
    togglePair,
    nameOverride,
    setNameOverride,
    filtered,
    present,
    error,
    nameForSlug,
    hasFilters,
    activeFilterCount,
    filtersOpen,
    setFiltersOpen,
    clearFilters,
    supersetTotal,
    commitAdd,
    commitSuperset,
    insertTemplate,
  } = useExercisePickerState({
    visible,
    supersetContext,
    presentSlugs,
    presentTreatment,
    apiFetch,
    cache,
    loadRecency,
    bookmarks,
    getExerciseName,
    onAddMany,
    onCreateSuperset,
    onClose,
  });

  const styles = makeStyles(theme);
  const isSuperset = !singleMode && tab === 'superset';

  // One label per treatment, resolved once instead of per row. The two say
  // different things because the two lists ARE different: the logger's set is
  // the whole workout, the composer's is only the block being edited (a
  // legitimate second block on another day is not "already there").
  const presentLabel =
    presentTreatment === 'mark-workout'
      ? t('mobile.logger.pickerAlreadyInWorkout', {
          defaultValue: 'Already in this workout',
        })
      : presentTreatment === 'mark-program'
        ? t('mobile.logger.pickerAlreadyInBlock', {
            defaultValue: 'Already in this block',
          })
        : null;

  // The superset panel and the filter rows SCROLL WITH the catalog instead
  // of sitting above it (LG-32, 2026-08-09). They used to be fixed siblings
  // of the list, and on the Superset tab the panel alone measured 968px of
  // a 1920px screen: the list — which carried no flex — was left with zero
  // height, so the only exercises still reachable were the "pair with"
  // chips, i.e. the ones already in the workout. Search and tabs stay
  // pinned: those are the tools you reach for constantly.
  //
  // Passed as an ELEMENT, never as an inline `() => (...)`. VirtualizedList
  // only reuses what `isValidElement` accepts and otherwise treats the
  // function as a component type, so a fresh identity each render would
  // remount this whole subtree on every keystroke in the search box.
  // Typing means you are browsing the catalog, so the panel's shortcuts get
  // out of the way: with the keyboard up the list is only ~350px tall and the
  // panel is ~1300, so leaving it in cost six swipes before the first match
  // appeared — measured on the redroid, and it was still the LG-32 symptom.
  // Clear the query and the shortcuts come back.
  const searching = query.trim() !== '';
  const listHeader = (
    <>
      {isSuperset && !searching ? (
        <SupersetPanel
          context={supersetContext}
          templates={templates}
          selectedPairIdxs={pairIdxs}
          onTogglePair={togglePair}
          onJoinGroup={(g) => {
            onJoinGroup?.(g);
            onClose();
          }}
          onInsertTemplate={insertTemplate}
          onRemoveTemplate={onRemoveTemplate}
          confirm={confirm}
          nameOf={nameForSlug}
          styles={styles}
        />
      ) : null}

      {filtersOpen ? (
        <>
          <FilterChipsRow
            theme={theme}
            label={t('catalog.region')}
            options={MuscleRegion.options}
            active={regions}
            onToggle={toggleRegion}
            renderLabel={(region) => t(`catalog.region_${region}`, { defaultValue: region })}
          />
          <FilterChipsRow
            theme={theme}
            label={t('catalog.equipment')}
            options={EquipmentKind.options}
            active={equipment}
            onToggle={toggleEquipment}
            renderLabel={(kind) => t(`catalog.equipment_${kind}`, { defaultValue: kind })}
          />
        </>
      ) : null}

      {hasFilters ? (
        <Pressable accessibilityRole="button" onPress={clearFilters} style={({ pressed }) => [styles.clearFilters, pressed && styles.clearFiltersPressed]}>
          <Text style={styles.clearFiltersText}>{t('mobile.logger.pickerClearFilters')}</Text>
        </Pressable>
      ) : null}
    </>
  );

  // Still three states, not one: `filtered === null` is "catalog not loaded
  // yet" and an empty array is "loaded, nothing matches". Both give the
  // FlatList a zero-length data array, so collapsing them here would show
  // "no exercises" while the fetch is still in flight.
  const listEmpty =
    filtered === null && error === null ? (
      <View style={styles.center}>
        <ActivityIndicator color={theme.palette.ink2} />
      </View>
    ) : error !== null ? (
      <View style={styles.center}>
        <Text style={styles.errorText}>{error}</Text>
      </View>
    ) : (
      // Same centring wrapper as the spinner and the error above it: the
      // three states have to occupy the residual space identically, or the
      // empty one hugs the filters with a screen of blank under it and
      // reads as a broken layout rather than as an answer.
      <View style={styles.center}>
        <EmptyState theme={theme} title={t('mobile.logger.pickerEmpty')} />
      </View>
    );

  return (
    <>
        {/* DS-42 — the place chip left the header's right cell and moved into
            its own named band below, because choosing a gym is a SESSION-wide
            gesture and the title row reads as chrome. ⛔ It moves here too, not
            only in the swap picker: Antenore's D2b rule is that add and replace
            use the same primitive in the same slot, so changing one alone would
            create exactly the drift that rule forbids. */}
        <View style={styles.header}>
          <Pressable
            accessibilityRole="button"
            onPress={onClose}
            style={styles.cancelHit}
            hitSlop={{ top: 12, bottom: 12, left: 16, right: 8 }}
          >
            <Text style={styles.cancel}>{t('mobile.logger.cancel')}</Text>
          </Pressable>
          <Text style={styles.title} numberOfLines={1}>
            {t('mobile.logger.pickerTitle')}
          </Text>
          <View style={styles.headerSpacer} />
        </View>
        <PickerSessionBand
          theme={theme}
          label={t('swap.bandSession', { defaultValue: 'Session' })}
          gymChip={gymChip}
        />

        {!singleMode ? (
        <View style={styles.tabBar}>
          {(['exercises', 'superset'] as PickerTab[]).map((tb) => {
            const active = tab === tb;
            return (
              <Pressable
                key={tb}
                accessibilityRole="button"
                accessibilityState={{ selected: active }}
                onPress={() => setTab(tb)}
                hitSlop={{ top: 8, bottom: 8 }}
                style={[styles.tab, active && styles.tabActive]}
              >
                <Text style={[styles.tabText, active && styles.tabTextActive]}>
                  {t(tb === 'exercises' ? 'mobile.logger.tabExercises' : 'mobile.logger.tabSuperset', {
                    defaultValue: tb === 'exercises' ? 'Exercises' : 'Superset',
                  })}
                </Text>
              </Pressable>
            );
          })}
        </View>
        ) : null}

        {/* Pinned, never scrolls: while you search for a partner you must not
            lose sight of WHO you are pairing with. Brand tint and not a fill,
            same as the chip it replaces — it states a fact, it is not a
            control (AP-0k2). */}
        {isSuperset && supersetContext?.currentName ? (
          <View style={styles.contextStrip}>
            <Text style={styles.panelLabel}>
              {t('mobile.superset.startingWith', {
                defaultValue: 'Starting superset with',
              })}
            </Text>
            <View style={[styles.chip, styles.chipAnchor, styles.contextChip]}>
              <Text style={styles.chipText} numberOfLines={1}>
                {supersetContext.currentName}
              </Text>
            </View>
          </View>
        ) : null}

        <View style={styles.searchWrap}>
          <TextInput
            style={styles.search}
            placeholder={t('mobile.logger.pickerSearch')}
            placeholderTextColor={theme.palette.ink3}
            autoCorrect={false}
            autoCapitalize="none"
            value={query}
            onChangeText={setQuery}
            // The list filters as you type, so the return key has nothing
            // left to submit — its one job is to get the keyboard out of
            // the way (LG-33).
            returnKeyType="search"
            onSubmitEditing={() => Keyboard.dismiss()}
          />
          <FiltersToggle
            theme={theme}
            open={filtersOpen}
            onToggle={() => setFiltersOpen(!filtersOpen)}
            activeCount={activeFilterCount}
            t={t}
          />
        </View>

        <FlatList
          style={styles.list}
          contentContainerStyle={styles.listContent}
          data={filtered ?? []}
          keyExtractor={(ex) => ex.slug}
          ListHeaderComponent={listHeader}
          ListEmptyComponent={listEmpty}
          renderItem={({ item }) => (
            <ExercisePickerRow
              exercise={item}
              name={getExerciseName(item.name)}
              selected={selected.has(item.slug)}
              multiSelect={!singleMode}
              bookmarked={bookmarks.isBookmarked(item.slug)}
              theme={theme}
              presentLabel={
                presentLabel !== null && present.has(item.slug) ? presentLabel : null
              }
              onPress={() => {
                if (singleMode) {
                  onPickSingle?.(item);
                  onClose();
                } else {
                  onRowPress(item);
                }
              }}
              onToggleStar={() => bookmarks.toggle(item.slug)}
              PainDot={PainDot}
              usePainRiskLabel={usePainRiskLabel}
              styles={styles}
            />
          )}
          ItemSeparatorComponent={() => <View style={[styles.separator, { backgroundColor: theme.palette.border }]} />}
          keyboardShouldPersistTaps="handled"
        />

        {!singleMode ? (
        <View style={styles.footer}>
          {isSuperset ? (
            <>
            <TextInput
              style={styles.nameInput}
              placeholder={t('mobile.superset.namePlaceholder', {
                defaultValue: 'Superset name (optional)',
              })}
              placeholderTextColor={theme.palette.ink3}
              value={nameOverride}
              onChangeText={setNameOverride}
              maxLength={60}
              returnKeyType="done"
            />
            <Pressable
              accessibilityRole="button"
              disabled={supersetTotal < 2}
              onPress={commitSuperset}
              style={[styles.footerBtn, supersetTotal < 2 && styles.footerBtnDisabled]}
            >
              <Text style={[styles.footerBtnText, supersetTotal < 2 && styles.footerBtnTextDisabled]}>
                {t('mobile.superset.createCta', { n: supersetTotal, defaultValue: 'Create superset ({{n}})' })}
              </Text>
            </Pressable>
            </>
          ) : (
            <Pressable
              accessibilityRole="button"
              disabled={selected.size === 0}
              onPress={commitAdd}
              style={[styles.footerBtn, selected.size === 0 && styles.footerBtnDisabled]}
            >
              <Text style={[styles.footerBtnText, selected.size === 0 && styles.footerBtnTextDisabled]}>
                {t('mobile.logger.pickerAddN', { n: selected.size, defaultValue: 'Add {{n}}' })}
              </Text>
            </Pressable>
          )}
        </View>
        ) : null}
    </>
  );
}
